import { inject, Injectable } from '@angular/core';
import { Subscription, forkJoin, Subject } from 'rxjs';

import { LeaderboardHttpService } from '../http/http.service';
import { UserSimpleGoal } from '../../model/user.model';
import { NonNullableFormBuilder } from '@angular/forms';

import { BroadcastService, BroadcastMessage } from '@app/core/service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class LeaderboardPreviewGoalService {
    private readonly leaderboardHttpService = inject(LeaderboardHttpService);
    private readonly broadcastService = inject(BroadcastService);
    private readonly translateService = inject(TranslateService);
    private readonly fb = inject(NonNullableFormBuilder);

    /**
     * Loading Flag
     */
    loading = false;

    /**
     * User
     */
    _user: Record<string, UserSimpleGoal | null> = {};

    /**
     * Users
     */
    _users: Record<string, UserSimpleGoal[] | null> = {};

    readonly form = this.fb.group({
        range: this.fb.control<LeaderboardPreviewGoalService['ranges'][number]>('week'),
    });

    /**
     * Range FormControl
     */
    readonly range = this.form.controls.range;

    /**
     * Ranges
     */
    readonly ranges = <const>['week', 'month', 'yeartodate', 'alltime'];

    /**
     * viewed User
     */
    viewedUser: UserSimpleGoal | null = null;

    /**
     * viewed Users
     */
    viewedUsers: UserSimpleGoal[] | null = null;

    /**
     * Subject
     */
    private updatesEvent = new Subject();

    /**
     * Subscriptions
     */
    private subscriptions = new Subscription();

    init(): void {
        this.subscriptions.add(this.broadcastService.broadcasts.subscribe((broadcast) => this.broadcasts(broadcast)));
    }

    destroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Updates Observable
     */
    get updates() {
        return this.updatesEvent.asObservable();
    }

    /**
     * Get Preview
     */
    leaderboard(count: number) {
        // create empty tree
        this.createEmptyTree();
        // check if page already loaded
        if (this._users[this.range.value]) {
            this.viewedUser = this._user[this.range.value];
            this.viewedUsers = this._users[this.range.value];
        } else {
            this.loadLeaderboard();
        }
        return this.sliceLeaderboard(count);
    }

    /**
     * Load Leaderboard
     */
    loadLeaderboard(): void {
        if (this.loading) {
            return;
        }

        const range = this.range.value;

        this.loading = true;
        this.updatesEvent.next(true);

        this.subscriptions.add(
            forkJoin([this.leaderboardHttpService.getUserGoal(range), this.leaderboardHttpService.getLeaderboardGoal(range, 10, 0)]).subscribe((data) =>
                this.leaderboardResults(range, data[0], data[1].users),
            ),
        );
    }

    /**
     * returns user's fullname (name + surname) or "me" if he/she is currently logged in
     * @param user user data
     */
    getUserFullName(user: UserSimpleGoal) {
        if (user.me) {
            return this.translateService.instant('v3-globale-me');
        }
        return `${user.firstname} ${user.surname}`;
    }

    /**
     * Trial - reset leaderboard
     */
    trialResetLeaderboard() {
        this.reset();
    }

    /**
     * Leaderboard Results Handler
     */
    private leaderboardResults(range: string, user: UserSimpleGoal, users: UserSimpleGoal[]) {
        this._users[range] = users;
        this._user[range] = user;
        this.loading = false;
        this.updatesEvent.next(true);
    }

    /**
     * Slice leaderboard
     */
    private sliceLeaderboard(count: number) {
        if (!this.viewedUsers || !this.viewedUser) {
            return null;
        }

        const users = this.viewedUsers.slice(0, count);
        const user = this.viewedUser;

        if (users.findIndex((userList) => userList.id === user.id) === -1) {
            users[users.length - 1] = user;
        }

        return users;
    }

    /**
     * Create Empty tree
     */
    private createEmptyTree(): void {
        // create object key
        if (!this._users[this.range.value]) {
            this._users[this.range.value] = null;
            this._user[this.range.value] = null;
        }
    }

    /**
     * Reset Pagination and Form
     */
    private reset(): void {
        this._user = {};
        this._users = {};
        this.viewedUser = null;
        this.viewedUsers = null;
    }

    /**
     * Broadcast
     */
    private broadcasts(broadcast: BroadcastMessage): void {
        switch (broadcast.message) {
            case 'episode-completed':
            case 'user-self-edited':
                this.reset();
                break;
        }
    }
}
